/** Sign in, sign out and "forgot password" (dialogs of every page). */

import { login, logout, requestPasswordReset } from "./api/client";
import { find, findAll, setMessage } from "./lib/dom";
import { formValue, isBusy, setBusy } from "./lib/forms";
import { ApiError, errorMessage } from "./lib/http";

const INVALID_LOGIN = "The email or password you have entered is incorrect.";

function initLogin(): void {
  const form = find(document, "[data-login-form]", HTMLFormElement);
  if (form === null) return;
  const error = find(form, "[data-form-error]", HTMLElement);
  const dialog = form.closest("dialog");
  dialog?.addEventListener("close", () => {
    form.reset();
    if (error) setMessage(error, "");
  });
  form.addEventListener("submit", (event) => {
    event.preventDefault();
    if (isBusy(form)) return;
    if (error) setMessage(error, "");
    const input = {
      email: formValue(form, "email").trim(),
      password: formValue(form, "password"),
      remember: formValue(form, "remember") === "1",
    };
    setBusy(form, true);
    login(input).then(
      () => {
        window.location.reload();
      },
      (failure: unknown) => {
        setBusy(form, false);
        const message =
          failure instanceof ApiError && failure.code === "invalid_credentials"
            ? INVALID_LOGIN
            : errorMessage(failure);
        if (error) setMessage(error, message);
        find(form, "[name=password]", HTMLInputElement)?.focus();
      },
    );
  });
}

function initForgot(): void {
  const form = find(document, "[data-forgot-form]", HTMLFormElement);
  if (form === null) return;
  const error = find(form, "[data-form-error]", HTMLElement);
  const success = find(form, "[data-form-success]", HTMLElement);
  form.closest("dialog")?.addEventListener("close", () => {
    form.reset();
    setBusy(form, false);
    if (error) setMessage(error, "");
    if (success) success.hidden = true;
  });
  form.addEventListener("submit", (event) => {
    event.preventDefault();
    if (isBusy(form)) return;
    if (error) setMessage(error, "");
    const email = formValue(form, "email").trim(); // before setBusy: disabled fields are not in FormData
    setBusy(form, true);
    requestPasswordReset(email).then(
      () => {
        form.setAttribute("aria-busy", "false");
        if (success) success.hidden = false;
      },
      (failure: unknown) => {
        setBusy(form, false);
        if (error) setMessage(error, errorMessage(failure));
      },
    );
  });
}

function initLogout(): void {
  for (const button of findAll(document, "[data-logout]", HTMLButtonElement)) {
    button.addEventListener("click", () => {
      button.disabled = true;
      logout().then(
        () => {
          window.location.assign("/");
        },
        () => {
          button.disabled = false;
          window.location.reload(); // the server state decides what the reloaded page shows
        },
      );
    });
  }
}

export function initAuthUi(): void {
  initLogin();
  initForgot();
  initLogout();
}
